import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import Icon from '../components/atoms/Icon';
import VerificationPopup from '../components/VerificationPopup';
import SessionManager from '../utils/sessionManager';
import { login as loginRequest } from '../services/api';
import { googleAuth } from '../services/googleAuth';
import { initiateLinkedInAuth } from '../services/linkedinAuth';
import { GOOGLE_CLIENT_ID, RECAPTCHA_SITE_KEY } from '../config';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Determine where to send the user after login.
  // Priority: ?redirect= query param -> location.state.from -> /candidate/dashboard
  const getPostLoginDestination = () => {
    const redirect = searchParams.get('redirect');
    if (redirect && redirect.startsWith('/')) return redirect;
    const fromState = location.state?.from;
    if (fromState && typeof fromState === 'string' && fromState.startsWith('/')) return fromState;
    return '/candidate/dashboard';
  };
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) console.error('❌ GOOGLE_CLIENT_ID is not set!');
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsSubmitting(true);
      setFeedback({ type: '', message: '' });
      const response = await googleAuth(credentialResponse.credential, 'candidate');
      SessionManager.setSession('candidate', response);
      setFeedback({ type: 'success', message: 'Welcome! Logging you in...' });
      if (response.role === 'candidate') setShowVerificationPopup(true);
      setTimeout(() => navigate(getPostLoginDestination()), 800);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Google login failed.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    setFeedback({ type: 'error', message: 'Google login failed. Please try again.' });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    if (!recaptchaToken) {
      setFeedback({ type: 'error', message: 'Please complete the reCAPTCHA verification.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await loginRequest({ email, password, role: 'candidate', recaptchaToken });
      SessionManager.setSession('candidate', response);
      setFeedback({ type: 'success', message: 'Welcome back!' });
      if (response.role === 'candidate') setShowVerificationPopup(true);
      setTimeout(() => navigate(getPostLoginDestination()), 800);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Unable to log in.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {showVerificationPopup && <VerificationPopup />}

      <div className="min-h-screen flex">

        {/* ==================== LEFT PANEL - Blue Theme with Image ==================== */}
        <div className="hidden lg:flex lg:w-[48%] relative bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
          {/* Gradient orbs */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/25 via-blue-500/20 to-transparent blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-indigo-500/25 via-purple-500/15 to-transparent blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-blue-400/10 blur-2xl"></div>
          </div>

          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNCkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-60 pointer-events-none"></div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
            {/* Top - Logo */}
            <div>
              <button onClick={() => navigate('/')} className="flex items-center space-x-2 group">
                <img 
                  src="https://image2url.com/r2/default/images/1772794265680-ac6ce145-0cef-45a6-bb44-c1923ebf64a1.png" 
                  alt="Aptirix Icon" 
                  className="w-20 h-20 rounded-xl"
                />
                <span className="text-xl font-bold text-white">Aptirix</span>
              </button>
            </div>

            {/* Center - Hero content + Image */}
            <div className="flex-1 flex flex-col justify-center space-y-8 py-12">
              <div>
                <div className="inline-block px-3 py-1.5 bg-cyan-400/15 rounded-full text-xs font-semibold text-cyan-300 border border-cyan-400/25 mb-6 tracking-wide">
                  AI-POWERED CAREER PLATFORM
                </div>
                <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                  Accelerate your<br />career with <span className="text-cyan-400">AI</span>
                </h1>
                <p className="text-gray-400 text-base max-w-sm leading-relaxed">
                  Practice interviews, build standout resumes, and get matched with opportunities tailored to your skills.
                </p>
              </div>

              {/* Image card */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30 max-w-sm">
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop&q=80"
                  alt="Professional working with AI tools"
                  className="w-full h-52 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-cyan-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-cyan-400/30">
                      <Icon name="trending_up" size={16} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">85% Interview Success Rate</p>
                      <p className="text-gray-400 text-xs">Powered by AI coaching</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Icon name="groups" size={20} className="text-cyan-400/70" />
                  <span className="text-gray-400 text-sm">50K+ Users</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="star" size={20} className="text-cyan-400/70" />
                  <span className="text-gray-400 text-sm">4.9 Rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="verified" size={20} className="text-cyan-400/70" />
                  <span className="text-gray-400 text-sm">Trusted</span>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <p className="text-gray-500 text-xs">© 2026 Aptirix. All rights reserved.</p>
          </div>
        </div>

        {/* ==================== RIGHT PANEL - White Professional Form ==================== */}
        <div className="flex-1 flex items-center justify-center bg-white px-6 sm:px-12 py-12">
          <div className="w-full max-w-[420px]">

            {/* Mobile logo */}
            <div className="lg:hidden flex items-center justify-center mb-8">
              <button onClick={() => navigate('/')} className="flex items-center space-x-2">
                <img 
                  src="https://image2url.com/r2/default/images/1772794265680-ac6ce145-0cef-45a6-bb44-c1923ebf64a1.png" 
                  alt="Aptirix Icon" 
                  className="w-30 h-30 rounded-xl"
                />
                <span className="text-xl font-bold text-gray-900">Aptirix</span>
              </button>
            </div>

            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
              <p className="text-gray-500 text-sm">Enter your credentials to access your account</p>
            </div>

            {/* Social Login - Top */}
            <div className="space-y-3 mb-6">
              <div className="w-full">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap
                  theme="outline"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>
              <button
                type="button"
                onClick={() => initiateLinkedInAuth('candidate')}
                className="w-full h-11 bg-white border border-gray-300 rounded-lg flex items-center justify-center space-x-2.5 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-all duration-150"
              >
                <svg className="w-5 h-5" fill="#0A66C2" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
                </svg>
                <span>Continue with LinkedIn</span>
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center mb-6">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="px-4 text-xs text-gray-400 uppercase tracking-wider font-medium">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <Icon name="mail" size={18} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-sm"
                    placeholder="name@company.com"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <Icon name="lock" size={18} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 pl-10 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-sm"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} />
                  </button>
                </div>
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="light"
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                  onErrored={() => setRecaptchaToken(null)}
                />
              </div>

              {/* Feedback */}
              {feedback.message && (
                <div className={`text-sm px-4 py-3 rounded-lg flex items-start space-x-2 ${
                  feedback.type === 'error'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}>
                  <Icon name={feedback.type === 'error' ? 'error' : 'check_circle'} size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{feedback.message}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!email || !password || !recaptchaToken || isSubmitting}
                className="w-full h-11 bg-[#0f172a] text-white text-sm font-semibold rounded-lg hover:bg-[#1e293b] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign in</span>
                )}
              </button>
            </form>

            {/* Sign up link */}
            <p className="text-center text-gray-500 text-sm mt-8">
              Don't have an account?{' '}
              <button onClick={() => navigate('/signup')} className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                Create an account
              </button>
            </p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
